import { createWithId, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { db, storage } from "../firebase";
import moment from "moment-timezone";
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import Address from './Address';
import { toE164PhoneNumber } from '../utils/stringFormatter';
import { toLower } from 'lodash';
import { ref, uploadBytes } from 'firebase/storage';

class Provider {
	static collectionName = 'providers';

	userId = null;

	email = null;
	phone = null;

	prefix = null;
	firstName = null;
	firstNameLower = null;
	middleName = null;
	lastName = null;
	lastNameLower = null;
	suffix = null;

	productIds = []; // list of ids of products (services) they can provide

	imageRefId = null;
	#image = null;

	bio = null;
	isVerified = false;

	specialtyIds = []; // list of specialty
	qualifications = []; // list of ProviderQualification objects
	affiliations = []; // list of ProviderAffiliation objects
	licenses = []; // list of ProviderLicense objects
	licenseStates = []; // list of states that the provider is licensed in based on the licenses field
	languages = []; // list of languages

	ein = null;
	npi = null;

	practiceFromYear = null; // year in which they started practicing medicine

	billingAddress = new Address(null).toDto();
	shippingAddress = new Address(null).toDto();

	defaultTimeSlot = 0; // time slot per virtual visit in minutes
	firstConsultationFee = 0; // fee for first consultation
	followUpConsultationFee = 0; // fee for follow up consultations

	inNetworkPayerCodes = [];
	inNetworkPayerStates = [];
	inNetworkPayers = []; // list of ProviderPayer objects (coveredEntityId, payerCode, state)

	avgRating = 0; // average rating of provider based on all reviews
	reviewCount = 0; // count of alll reviews for the provider

	constructor(provider) {
		if (isObjectNotEmpty(provider)) {
			if (isStringNotEmpty(provider.userId)) { this.userId = provider.userId; }

			if (isStringNotEmpty(provider.email)) { this.email = toLower(provider.email); }
			if (isStringNotEmpty(provider.phone)) { this.phone = toE164PhoneNumber(provider.phone); }

			if (isStringNotEmpty(provider.firstName)) {
				this.firstName = provider.firstName;
				this.firstNameLower = toLower(provider.firstName);
			}
			if (isStringNotEmpty(provider.lastName)) {
				this.lastName = provider.lastName;
				this.lastNameLower = toLower(provider.lastName);
			}
			if (isStringNotEmpty(provider.prefix)) { this.prefix = provider.prefix; }
			if (isStringNotEmpty(provider.middleName)) { this.middleName = provider.middleName; }
			if (isStringNotEmpty(provider.suffix)) { this.suffix = provider.suffix; }

			if (isListNotEmpty(provider.productIds)) { this.productIds = provider.productIds; }

			if (isStringNotEmpty(provider.imageRefId)) { this.imageRefId = provider.imageRefId; }
			if (isNotNullOrUndefined(provider.image)) { this.#image = provider.image; }

			if (isStringNotEmpty(provider.bio)) { this.bio = provider.bio; }
			if (isBooleanTrue(provider.isVerified)) { this.isVerified = true; }

			if (isListNotEmpty(provider.specialtyIds)) { this.specialtyIds = provider.specialtyIds; }
			if (isListNotEmpty(provider.qualifications)) { this.qualifications = provider.qualifications; }
			if (isListNotEmpty(provider.affiliations)) { this.affiliations = provider.affiliations; }
			if (isListNotEmpty(provider.licenses)) { this.licenses = provider.licenses; }
			if (isListNotEmpty(provider.languages)) { this.languages = provider.languages; }

			if (isStringNotEmpty(provider.ein)) { this.ein = provider.ein.replace(/\D/g, ''); }
			if (isStringNotEmpty(provider.npi)) { this.npi = provider.npi; }

			if (isNumberGreaterThanOrEqualToZero(provider.practiceFromYear)) { this.practiceFromYear = provider.practiceFromYear; }

			if (isObjectNotEmpty(provider.billingAddress)) { this.billingAddress = new Address(provider.billingAddress).toDto(); }
			if (isObjectNotEmpty(provider.shippingAddress)) { this.shippingAddress = new Address(provider.shippingAddress).toDto(); }

			if (isNumberGreaterThanOrEqualToZero(provider.defaultTimeSlot)) { this.defaultTimeSlot = provider.defaultTimeSlot; }
			if (isNumberGreaterThanOrEqualToZero(provider.firstConsultationFee)) { this.firstConsultationFee = provider.firstConsultationFee; }
			if (isNumberGreaterThanOrEqualToZero(provider.followUpConsultationFee)) { this.followUpConsultationFee = provider.followUpConsultationFee; }

			if (isListNotEmpty(provider.inNetworkPayerCodes)) { this.inNetworkPayerCodes = provider.inNetworkPayerCodes; }

			if (isNumberGreaterThanOrEqualToZero(provider.avgRating)) { this.avgRating = provider.avgRating; }
			if (isNumberGreaterThanOrEqualToZero(provider.reviewCount)) { this.reviewCount = provider.reviewCount; }
		}
	}
	toDto = () => {
		return {
			userId: this.userId,

			email: this.email,
			phone: this.phone,

			prefix: this.prefix,
			firstName: this.firstName,
			firstNameLower: this.firstNameLower,
			middleName: this.middleName,
			lastName: this.lastName,
			lastNameLower: this.lastNameLower,
			suffix: this.suffix,

			productIds: this.productIds,

			imageRefId: this.imageRefId,

			bio: this.bio,
			isVerified: this.isVerified,

			specialtyIds: this.specialtyIds,
			qualifications: this.qualifications,
			affiliations: this.affiliations,
			licenses: this.licenses,
			languages: this.languages,

			ein: this.ein,
			npi: this.npi,

			practiceFromYear: this.practiceFromYear,

			shippingAddress: this.shippingAddress,
			billingAddress: this.billingAddress,

			defaultTimeSlot: this.defaultTimeSlot,
			firstConsultationFee: this.firstConsultationFee,
			followUpConsultationFee: this.followUpConsultationFee,

			inNetworkPayerCodes: this.inNetworkPayerCodes,

			avgRating: this.avgRating,
			reviewCount: this.reviewCount,
		};
	}

	static addFile = async (file, userId) => {
		if (isNotNullOrUndefined(file) && isStringNotEmpty(userId)) {
			try {
				const fileName = userId + '.' + this.getFileExt(file);
				const storageRef = ref(storage, 'providers/' + fileName);

				return await uploadBytes(storageRef, file);
			} catch (err) {
				console.log(err);
			}
		}

		return null;
	}

	static getFileExt = (file) => {
		const lastDot = file.name.lastIndexOf('.');
		//const fileName = fullFileName.substring(0, lastDot);
		const ext = file.name.substring(lastDot + 1);

		return toLower(ext);
	}

	static uploadImage = async (file, userId) => {
		if (isNotNullOrUndefined(file) && isStringNotEmpty(userId)) {
			const uploadRes = await this.addFile(file, userId);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				return uploadRes.ref.fullPath;
			}
		}

		return null;
	}

	isValid = () => {
		if (isStringEmpty(this.userId)) {
			throw new Error('userId is required.');
		}

		return true;
	}

	create = async () => {
		if (this.isValid()) {
			if (isNotNullOrUndefined(this.#image)) {
				this.imageRefId = await Provider.uploadImage(this.#image, this.userId);
			}
			return await createWithId(Provider.collectionName, this.userId, this.toDto());
		}

		return null;
	}

	static update = async (id, updatedProvider, oldProvider) => {
		let updatedObj = new Provider(updatedProvider);
		if (isNotNullOrUndefined(updatedObj.#image)) {
			updatedObj.imageRefId = await this.uploadImage(updatedObj.#image, updatedObj.userId);
		}
		const success = await update(Provider.collectionName, id, updatedObj.toDto());
		if (isBooleanTrue(success)) {
			return { ...oldProvider, ...updatedObj.toDto() };
		}

		return null;
	}

	static updateField = async (id, fieldName, newValue) => {
		return await updateField(Provider.collectionName, id, fieldName, newValue);
	}

	static delete = async (id) => {
		return await remove(Provider.collectionName, id);
	}

	static getById = async (id) => {
		return await getById(Provider.collectionName, id);
	}

	static getAll = async () => {
		return await getAll(Provider.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer) => {
		return await getBySingleQuery(Provider.collectionName, fieldName, fieldValue, comparer);
	}

	static getByIds = async (ids) => {
		return await getByIdQuery(Provider.collectionName, ids, 'in');
	}

	static getCount = async (queryParameters) => {
		const { state, postalCode, productId, isVerified, specialtyId, language, payerCode, practiceFromYear } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', 'in', postalCode));
		} else if (isStringNotEmptyOrZeroLength(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', '==', postalCode));
		}

		if (isListNotEmpty(productId)) {
			queryConstraints.push(where('productIds', 'array-contains-any', productId));
		} else if (isStringNotEmptyOrZeroLength(productId)) {
			queryConstraints.push(where('productIds', 'array-contains', productId));
		}

		if (isNotNullOrUndefined(isVerified)) {
			if (isBooleanTrue(isVerified)) {
				queryConstraints.push(where('isVerified', '==', true));
			} else if (isBooleanFalse(isVerified)) {
				queryConstraints.push(where('isVerified', '==', false));
			}
		}

		if (isListNotEmpty(specialtyId)) {
			queryConstraints.push(where('specialtyIds', 'array-contains-any', specialtyId));
		} else if (isStringNotEmptyOrZeroLength(specialtyId)) {
			queryConstraints.push(where('specialtyIds', 'array-contains', specialtyId));
		}

		if (isListNotEmpty(language)) {
			queryConstraints.push(where('languages', 'array-contains-any', language));
		} else if (isStringNotEmptyOrZeroLength(language)) {
			queryConstraints.push(where('languages', 'array-contains', language));
		}

		if (isListNotEmpty(payerCode)) {
			queryConstraints.push(where('inNetworkPayerCodes', 'array-contains-any', payerCode));
		} else if (isStringNotEmptyOrZeroLength(payerCode)) {
			queryConstraints.push(where('inNetworkPayerCodes', 'array-contains', payerCode));
		}

		if (isNumberNotEmpty(practiceFromYear)) {
			queryConstraints.push(where('practiceFromYear', '<=', practiceFromYear));
		}

		let q = query(collection(db, Provider.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { state, postalCode, productId, isVerified, specialtyId, language, payerCode, practiceFromYear } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', 'in', postalCode));
		} else if (isStringNotEmptyOrZeroLength(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', '==', postalCode));
		}

		if (isListNotEmpty(productId)) {
			queryConstraints.push(where('productIds', 'array-contains-any', productId));
		} else if (isStringNotEmptyOrZeroLength(productId)) {
			queryConstraints.push(where('productIds', 'array-contains', productId));
		}

		if (isNotNullOrUndefined(isVerified)) {
			if (isBooleanTrue(isVerified)) {
				queryConstraints.push(where('isVerified', '==', true));
			} else if (isBooleanFalse(isVerified)) {
				queryConstraints.push(where('isVerified', '==', false));
			}
		}

		if (isListNotEmpty(specialtyId)) {
			queryConstraints.push(where('specialtyIds', 'array-contains-any', specialtyId));
		} else if (isStringNotEmptyOrZeroLength(specialtyId)) {
			queryConstraints.push(where('specialtyIds', 'array-contains', specialtyId));
		}

		if (isListNotEmpty(language)) {
			queryConstraints.push(where('languages', 'array-contains-any', language));
		} else if (isStringNotEmptyOrZeroLength(language)) {
			queryConstraints.push(where('languages', 'array-contains', language));
		}

		if (isListNotEmpty(payerCode)) {
			queryConstraints.push(where('inNetworkPayerCodes', 'array-contains-any', payerCode));
		} else if (isStringNotEmptyOrZeroLength(payerCode)) {
			queryConstraints.push(where('inNetworkPayerCodes', 'array-contains', payerCode));
		}

		if (isNumberNotEmpty(practiceFromYear)) {
			queryConstraints.push(where('practiceFromYear', '<=', practiceFromYear));
			orderByField = 'practiceFromYear';
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, Provider.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, Provider.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collection(db, Provider.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;
			let totalPages = 0;

			let totalCount = await this.getCount(queryParameters);
			totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default Provider;